import { NO } from "uicore-ts"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import type { RHCompanyMember } from "../../SocketClientFunctions"


export interface RHCompanyNamesPresentation {
    text: string
    tooltip: string
}


export interface RHCompanyMemberIdentityPresentation {
    /** Normalized plain text for comparisons, sorting and native textContent controls. */
    displayName?: string
    email: string
    /** Entity-escaped values for UITextView, whose text setter writes innerHTML. */
    escapedDisplayName?: string
    escapedEmail: string
    text: string
    searchText: string
}


export class RHCompanyMemberPresentation {

    static administratorRoleExplanationForProcurerEligibility(
        isEligibleToActAsProcurer: boolean,
        shouldIncludeRepresentativeGuidance: boolean = NO
    ): string {
        let explanation = "Kutsub ettevõttesse liikmeid, muudab nende rolle ja eemaldab neid. "
        if (isEligibleToActAsProcurer) {
            explanation += "Saab ühtlasi hankija ja pakkuja esindaja rolli."
            if (shouldIncludeRepresentativeGuidance) {
                explanation += " Hangete ja pakkumuste tegemiseks piisab esindaja rollist."
            }
        }
        else {
            explanation += "Saab ühtlasi pakkuja esindaja rolli."
            if (shouldIncludeRepresentativeGuidance) {
                explanation += " Pakkumuste tegemiseks piisab esindaja rollist."
            }
        }
        return explanation
    }


    static identityPresentationForPerson(person: {
        name?: string | null
        email?: string | null
    }): RHCompanyMemberIdentityPresentation {
        const name = person.name?.trim() ?? ""
        const email = person.email?.trim() ?? ""
        let displayName: string | undefined
        let escapedDisplayName: string | undefined
        if (name.length > 0 && name.toLowerCase() !== email.toLowerCase()) {
            displayName = name
            escapedDisplayName = RHTextSanitizer.escapedHTML(displayName)
        }
        const escapedEmail = RHTextSanitizer.escapedHTML(email)
        const identityParts = [escapedDisplayName, escapedEmail].filter(
            (value): value is string => value !== undefined && value.length > 0
        )
        return {
            displayName,
            email,
            escapedDisplayName,
            escapedEmail,
            text: identityParts.join("\n"),
            searchText: [displayName, email].filter(
                (value): value is string => value !== undefined && value.length > 0
            ).join(" ")
        }
    }

    static companyNamesPresentationForMember(
        member: Pick<RHCompanyMember, "companies">
    ): RHCompanyNamesPresentation {
        const companyNames = member.companies
            .map(company => company.name.trim())
            .filter(companyName => companyName.length > 0)
            .sort((firstCompanyName, secondCompanyName) =>
                firstCompanyName.localeCompare(secondCompanyName, "et")
            )
        return {
            // `text` renders through a UITextView (an innerHTML sink); `tooltip` renders
            // through a plain-text hover attribute, so only `text` needs escaping.
            text: companyNames.map(companyName => RHTextSanitizer.escapedHTML(companyName)).join("\n"),
            tooltip: companyNames.join("\n")
        }
    }
}
